import { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { AuthContext } from '../auth/AuthContext';
import api from '../api/axios';
import { FiEye, FiEyeOff } from 'react-icons/fi';

const ROLE_LABELS = {
  member: 'Team Member',
  developer: 'Developer',
  tester: 'QA / Tester',
  designer: 'Designer',
  devops: 'DevOps',
  manager: 'Manager',
  product_manager: 'Product Manager',
  admin: 'Admin',
};

const AcceptInvite = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const { login } = useContext(AuthContext);

  const [invite, setInvite] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [allowedDomains, setAllowedDomains] = useState(null);
  const [emailDomainError, setEmailDomainError] = useState('');

  const [form, setForm] = useState({
    full_name: '',
    email: '',
    password: '',
    confirm: '',
  });

  useEffect(() => {
    // Fetch allowed domains config
    api.get('/auth/config').then(r => {
      if (r.data.allowedEmailDomains) setAllowedDomains(r.data.allowedEmailDomains);
    }).catch(() => {});

    api.get(`/auth/invite/${token}`)
      .then((r) => {
        setInvite(r.data);
        if (r.data.email) setForm((f) => ({ ...f, email: r.data.email }));
      })
      .catch((err) => setError(err.response?.data?.message || 'Invalid or expired invite link'))
      .finally(() => setLoading(false));
  }, [token]);

  const validateEmailDomain = (email) => {
    if (!allowedDomains || !email || !email.includes('@')) { setEmailDomainError(''); return true; }
    const domain = email.split('@')[1]?.toLowerCase();
    const ok = allowedDomains.map(d => d.toLowerCase()).includes(domain);
    if (!ok) {
      setEmailDomainError(`Only ${allowedDomains.map(d => '@' + d).join(', ')} email addresses are allowed`);
      return false;
    }
    setEmailDomainError('');
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.password !== form.confirm) { setError('Passwords do not match'); return; }
    if (form.password.length < 6) { setError('Password must be at least 6 characters'); return; }
    if (!validateEmailDomain(form.email)) return;
    setError('');
    setSaving(true);
    try {
      const body = {
        full_name: form.full_name,
        email: form.email,
        password: form.password,
      };
      const res = await api.post(`/auth/accept-invite/${token}`, body);
      
      // Check if user needs approval
      if (res.data.status === 'pending') {
        // Don't login, just redirect to pending approval page
        navigate('/pending-approval');
      } else {
        // User is approved, login and go to dashboard
        login(res.data.token, res.data.role, {
          id: res.data.userId,
          email: res.data.email,
          role: res.data.role,
          needsOnboarding: false,
        });
        navigate('/dashboard');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Something went wrong');
    }
    setSaving(false);
  };

  return (
    <div style={{ minHeight: '100vh', background: 'linear-gradient(135deg, #2C63AE 0%, #1272B3 100%)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '2rem', fontFamily: "'Inter', sans-serif" }}>

      <div style={{ background: '#fff', borderRadius: 20, padding: '2.5rem 2.5rem',
        width: '100%', maxWidth: 420, boxShadow: '0 30px 80px rgba(0,0,0,0.35)' }}>

        <div style={{ textAlign: 'center', marginBottom: 28 }}>
          <img src="/company-logo.png" alt="Company Logo" style={{ height: 48, marginBottom: 8 }} />
          <div style={{ fontSize: '1.75rem', fontWeight: 800, color: '#1272B3' }}>Team Space</div>
          <div style={{ color: '#64748b', fontSize: '0.85rem', marginTop: 4 }}>You've been invited to join</div>
        </div>

        {loading ? (
          <div style={{ textAlign: 'center', color: '#94a3b8', padding: '2rem' }}>Validating invite…</div>
        ) : error && !invite ? (
          <div style={{ textAlign: 'center' }}>
            <div style={{ width: 48, height: 48, borderRadius: '50%', background: '#fee2e2', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 12px', color: '#dc2626', fontSize: '1.4rem', fontWeight: 700 }}>✕</div>
            <div style={{ fontWeight: 700, color: '#dc2626', marginBottom: 8 }}>{error}</div>
            <div style={{ fontSize: '0.82rem', color: '#94a3b8', marginBottom: 20 }}>
              This link may have expired or already been used. Ask your Manager/Admin to send a new one.
            </div>
            <button type="button" onClick={() => navigate('/')}
              style={{ padding: '8px 20px', background: '#1272B3', color: '#fff',
                border: 'none', borderRadius: 8, fontWeight: 700, cursor: 'pointer' }}>
              Back to Login
            </button>
          </div>
        ) : (
          <>
            <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 10,
              padding: '12px 16px', marginBottom: 24 }}>
              <div style={{ fontWeight: 700, color: '#15803d', fontSize: '0.9rem' }}>
                Welcome to the team!
              </div>
              {invite?.department && (
                <div style={{ fontSize: '0.78rem', color: '#64748b', marginTop: 4 }}>
                  Department: <strong>{invite.department}</strong>
                </div>
              )}
              {invite?.projectname && (
                <div style={{ fontSize: '0.78rem', color: '#1272B3', marginTop: 4, fontWeight: 600 }}>
                  You'll be added to: {invite.projectname}
                </div>
              )}
            </div>

            <form onSubmit={handleSubmit}>
              <div style={{ marginBottom: 14 }}>
                <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 600, color: '#374151', marginBottom: 4 }}>
                  Your full name *
                </label>
                <input value={form.full_name} onChange={(e) => setForm((f) => ({ ...f, full_name: e.target.value }))}
                  placeholder="e.g. John Smith"
                  required
                  autoFocus
                  style={{ width: '100%', padding: '10px 12px', border: '1px solid #e2e8f0',
                    borderRadius: 8, fontSize: '0.9rem', boxSizing: 'border-box', background: '#f8fafc' }} />
              </div>

              <div style={{ marginBottom: 14 }}>
                <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 600, color: '#374151', marginBottom: 4 }}>
                  Email *
                </label>
                <input
                  value={form.email}
                  onChange={(e) => { setForm((f) => ({ ...f, email: e.target.value })); validateEmailDomain(e.target.value); }}
                  type="email" placeholder="your@company.com"
                  required disabled={!!invite?.email}
                  style={{ width: '100%', padding: '10px 12px', border: `1px solid ${emailDomainError ? '#fca5a5' : '#e2e8f0'}`,
                    borderRadius: 8, fontSize: '0.9rem', boxSizing: 'border-box',
                    background: invite?.email ? '#f1f5f9' : '#f8fafc',
                    color: invite?.email ? '#64748b' : '#0f172a' }} />
                {invite?.email ? (
                  <div style={{ fontSize: '0.72rem', color: '#94a3b8', marginTop: 3 }}>
                    Email pre-filled from your invite
                  </div>
                ) : allowedDomains && (
                  <div style={{ fontSize: '0.72rem', color: '#64748b', marginTop: 3 }}>
                    Allowed: {allowedDomains.map(d => '@' + d).join(', ')}
                  </div>
                )}
                {emailDomainError && (
                  <div style={{ fontSize: '0.75rem', color: '#dc2626', fontWeight: 600, marginTop: 4 }}>
                    ⚠ {emailDomainError}
                  </div>
                )}
              </div>

              <div style={{ marginBottom: 14 }}>
                <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 600, color: '#374151', marginBottom: 4 }}>
                  Password *
                </label>
                <div style={{ position: 'relative' }}>
                  <input value={form.password} onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
                    type={showPassword ? 'text' : 'password'} placeholder="Min 6 characters" required
                    style={{ width: '100%', padding: '10px 40px 10px 12px', border: '1px solid #e2e8f0',
                      borderRadius: 8, fontSize: '0.9rem', boxSizing: 'border-box', background: '#f8fafc' }} />
                  <button type="button" onClick={() => setShowPassword(v => !v)}
                    style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)',
                      background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', padding: '0.25rem', display: 'flex', alignItems: 'center' }}>
                    {showPassword ? <FiEyeOff size={18} /> : <FiEye size={18} />}
                  </button>
                </div>
              </div>

              <div style={{ marginBottom: 20 }}>
                <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 600, color: '#374151', marginBottom: 4 }}>
                  Confirm password *
                </label>
                <div style={{ position: 'relative' }}>
                  <input value={form.confirm} onChange={(e) => setForm((f) => ({ ...f, confirm: e.target.value }))}
                    type={showConfirm ? 'text' : 'password'} placeholder="Repeat password" required
                    style={{ width: '100%', padding: '10px 40px 10px 12px', border: '1px solid #e2e8f0',
                      borderRadius: 8, fontSize: '0.9rem', boxSizing: 'border-box', background: '#f8fafc' }} />
                  <button type="button" onClick={() => setShowConfirm(v => !v)}
                    style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)',
                      background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', padding: '0.25rem', display: 'flex', alignItems: 'center' }}>
                    {showConfirm ? <FiEyeOff size={18} /> : <FiEye size={18} />}
                  </button>
                </div>
              </div>

              {error && (
                <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8,
                  padding: '10px 12px', marginBottom: 16, color: '#dc2626', fontSize: '0.82rem', fontWeight: 600 }}>
                  {error}
                </div>
              )}

              <button type="submit" disabled={saving}
                style={{ width: '100%', padding: '12px', background: '#1272B3', color: '#fff',
                  border: 'none', borderRadius: 10, fontWeight: 800, fontSize: '1rem', cursor: 'pointer',
                  opacity: saving ? 0.7 : 1 }}>
                {saving ? 'Creating account…' : 'Join Team Space →'}
              </button>
            </form>

            <div style={{ textAlign: 'center', marginTop: 16, fontSize: '0.78rem', color: '#94a3b8' }}>
              Already have an account?{' '}
              <button type="button" onClick={() => navigate('/')}
                style={{ background: 'none', border: 'none', color: '#1272B3', fontWeight: 600, cursor: 'pointer', fontSize: '0.78rem' }}>
                Log in
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default AcceptInvite;
